import { useRef, useEffect } from 'react';
import { stylefunction } from 'ol-mapbox-style';
import { defaultLoadFunction } from 'ol/source/VectorTile';
import OLVectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import MVT from 'ol/format/MVT';
import TileGrid from 'ol/tilegrid/TileGrid';
import { createXYZ } from 'ol/tilegrid';
import { useOLMap } from '../MapContext';
import { useLayerEffect } from './useLayerEffect';
import { styleFactory, styleFilter } from '../../utils/styleFactory';
// feature.id is needet because of mapbox-style - apply.js
const FEATURE_HIGHTLIGHT_ID = '_fakeFeatureHighlightId';
export const VectorTileLayer = (props) => {
    const map = useOLMap();
    const layerRef = useRef();
    useLayerEffect(layerRef, props.layerOptions);
    useEffect(() => {
        const projection = map.getView().getProjection();
        const tileLoadFunction = (tile, urlString) => {
            const params = props.getUrlParams?.();
            if (params) {
                const url = new URL(urlString, window.location.href);
                for (const key in params) {
                    url.searchParams.set(key, params[key]);
                }
                // LAYERS
                if (props.sourceOptions.params?.layers) {
                    url.searchParams.set('layers', props.sourceOptions.params.layers);
                }
                urlString = url.toString();
            }
            return defaultLoadFunction(tile, urlString);
        };
        let tileGrid = undefined;
        if (props.gridParams && props.gridParams.resolutions && props.gridParams.origin) {
            tileGrid = new TileGrid({
                origin: props.gridParams.origin,
                resolutions: props.gridParams.resolutions,
                tileSize: props.gridParams.tileSize,
            });
        }
        else if (props.gridParams && props.gridParams.extent) {
            tileGrid = createXYZ({
                extent: props.gridParams.extent,
                tileSize: props.gridParams.tileSize,
            });
        }
        const layer = new OLVectorTileLayer({
            ...{ id: props.id },
            visible: props.layerOptions.visible,
            opacity: props.layerOptions.opacity ?? 1,
            minResolution: props.layerOptions.minResolution,
            maxResolution: props.layerOptions.maxResolution,
            zIndex: props.layerOptions.zIndex,
            renderBuffer: 30, // should be same as on the server and as maximum size of the map symbol
            source: new VectorTileSource({
                attributions: props.sourceOptions.attributions,
                projection: props.sourceOptions.projection ?? projection,
                url: props.sourceOptions.url,
                format: new MVT(),
                tileGrid,
                tileLoadFunction,
            }),
        });
        layerRef.current = layer;
        if (props.mapboxStyle) {
            const sourceOrLayers = Object.keys(props.mapboxStyle.sources);
            stylefunction(layer, props.mapboxStyle, sourceOrLayers);
        }
        map.addLayer(layer);
        if (process.env.NODE_ENV === 'development') {
            console.debug('new VectorTileLayer', map, layerRef.current);
        }
        return () => {
            // unmount
            const layer = layerRef.current;
            if (layer) {
                map.removeLayer(layer);
                layer.dispose();
                layerRef.current = null;
            }
        };
    }, [map]);
    useEffect(() => {
        // user defined mapbox styles
        let mapboxStyleFunction;
        const layer = layerRef.current;
        if (!layer)
            return;
        if (props.mapboxStyle) {
            const sourceOrLayers = Object.keys(props.mapboxStyle.sources);
            mapboxStyleFunction = stylefunction(layer, props.mapboxStyle, sourceOrLayers);
        }
        // default predefined ol styles
        const hiddenFeatureIds = new Set(props.hiddenFeatureIds);
        const highlightedFeatureIds = new Set(props.highlightedFeatureIds);
        const highlightFeatureStyle = styleFilter((feature) => highlightedFeatureIds.has((feature.getId() ?? feature.get('id'))), styleFactory('HIGHLIGHT'), mapboxStyleFunction ?? styleFactory('DEFAULT'));
        const style = styleFilter((feature) => hiddenFeatureIds.has((feature.getId() ?? feature.get('id'))), [], highlightFeatureStyle);
        const styleFunctionWithFixedFeature = (feature, resolution) => {
            if (!feature.getId()) {
                // is needet because of mapbox-style - stylefunction.js
                feature.id_ = feature.get('id') ?? FEATURE_HIGHTLIGHT_ID;
            }
            const styleWithZIndex = style(feature, resolution);
            styleWithZIndex.forEach((style) => style.setZIndex(Infinity));
            return styleWithZIndex;
        };
        layer.setStyle(styleFunctionWithFixedFeature);
    }, [props.mapboxStyle, props.id, props.hiddenFeatureIds, props.highlightedFeatureIds]);
    useEffect(() => {
        layerRef.current?.getSource()?.refresh();
    }, [map, props.version]);
    return null;
};
//# sourceMappingURL=VectorTileLayer.js.map